import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import RfiCreatedSuccessScreen from './components/RfiCreatedSuccessScreen';
import RfiCreateActions from './components/RfiCreateActions';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import utils, {
  getArrayLength,
  getArrayOfEmails,
  getCheckedValuesInObject,
} from 'utils';
import { getIsStartOfDayToday } from 'dateUtils';
import RfiContext from 'router/subrouters/DataRoom/providers/Rfi/Rfi.context';
import {
  DataRoomRFI,
  PARSED_SPREADSHEET_RFI_ROW_STATUS,
  ParsedSpreadsheetRFIRow,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import ScrollToContext from 'providers/ScrollTo/ScrollTo.context';
import useCreateRfi from '../../hooks/useCreateRfi';
import {
  getReadyDocumentsCount,
  hasSelectedDocument,
  isNotFirstPage,
  isSpreadsheetMethod,
  RFI_CREATE_STEPS,
} from './utils';
import { Button } from 'ncoded-component-library';
import ArrowleftIcon from 'icons/Arrowleft.icon';

import './RfiCreate.styles.scss';
import './RfiCreate.styles.responsive.scss';

type RfiCreateProps = {
  className?: string;
};

const RfiCreate: React.FC<RfiCreateProps> = (props) => {
  const { className } = props;

  const [showSuccess, setShowSuccess] = useState(false);
  const [createdRfi, setCreatedRfi] = useState<DataRoomRFI>();
  const [step, setStep] = useState<number>(RFI_CREATE_STEPS.DOCUMENTS);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);
  const { refetch, refetchTemplates } = useContext(RfiContext);
  const { scrollToTop } = useContext(ScrollToContext);

  const { t } = useTranslation();

  const classes = classNames('rfi-create', 'white-panel', className);

  const { initialValues, stepsByPage } = useCreateRfi(false);

  const handleOnSubmit = useCallback(
    async (values) => {
      const {
        documents,
        customDocuments,
        notificationMedium,
        recipients,
        recipientsUnsubmited,
        dueDate,
        dueTime,
        saveAsTemplate,
        saveAsTemplateName,
        startFromTemplateId,
        ...restValues
      } = values;

      const { creationType } = restValues;

      const documentsFinal = isSpreadsheetMethod(creationType)
        ? (documents as ParsedSpreadsheetRFIRow[])
            .filter(
              ({ status }) =>
                status === PARSED_SPREADSHEET_RFI_ROW_STATUS.READY_TO_ADD,
            )
            .map(({ document, assignedTo, note }) => ({
              name: document,
              assignedTo,
              note,
            }))
        : [...documents, ...customDocuments]
            .filter(({ checked }) => checked)
            .map(({ name, period }) => ({ name, period }));

      const notificationMediumFinal =
        getCheckedValuesInObject(notificationMedium);

      const finalEmails = getArrayOfEmails(recipients, recipientsUnsubmited);

      const finalDate =
        dueDate && dueTime
          ? utils.getDateInUserTimezone(dueDate.toISOString(), +dueTime)
          : dueDate
          ? dueDate.toISOString()
          : undefined;

      const data = {
        documents: documentsFinal,
        notifyOnComplete: !!notificationMediumFinal.length,
        notificationMedium: notificationMediumFinal,
        recipients: finalEmails,
        dueDate: finalDate,
        saveAsTemplate,
        ...(saveAsTemplate && { saveAsTemplateName }),
        ...restValues,
      };

      try {
        const { data: rfi } = await dataRoomApi.rfi.createRfi(_id, data);

        setCreatedRfi(rfi);
        setShowSuccess(true);
        refetch();

        if (saveAsTemplate) {
          refetchTemplates();
        }
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, refetch, refetchTemplates, popServerError],
  );

  const goToStep = useCallback(
    (nextStep: number) => {
      setStep(nextStep);
      scrollToTop();
    },
    [scrollToTop],
  );

  const handleNext = useCallback(
    () => goToStep(RFI_CREATE_STEPS.DETAILS),
    [goToStep],
  );

  const validate = (values: any) => {
    const errors: Record<string, string> = {};

    const { dueDate, dueTime } = values;

    if (getIsStartOfDayToday(dueDate) && !dueTime) {
      errors.dueTime = t('requiredField');
    }

    return errors;
  };

  return (
    <div className={classes}>
      {showSuccess ? (
        <RfiCreatedSuccessScreen
          rfi={createdRfi}
          onRfiUpdate={(updatedRfi) =>
            setCreatedRfi((prev) => ({ ...prev, ...updatedRfi }))
          }
          onCreateNew={() => {
            setShowSuccess(false);
            setStep(RFI_CREATE_STEPS.DOCUMENTS);
          }}
        />
      ) : (
        <>
          {isNotFirstPage(step) && (
            <Button
              variant="link"
              icon={ArrowleftIcon}
              onClick={() => goToStep(RFI_CREATE_STEPS.DOCUMENTS)}
            >
              {t('backToDocuments')}
            </Button>
          )}
          <Form
            onSubmit={handleOnSubmit}
            initialValues={initialValues}
            validate={validate}
            render={({ submitting, values, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                {stepsByPage[step]
                  .filter(
                    ({ hideForSpreadsheet }) =>
                      !hideForSpreadsheet ||
                      !isSpreadsheetMethod(values.creationType),
                  )
                  .map(({ node }) => node)}

                <RfiCreateActions
                  step={step}
                  submitDisabled={submitting}
                  nextDisabled={
                    !hasSelectedDocument(
                      values.documents,
                      values.customDocuments,
                      values.creationType,
                    )
                  }
                  {...(isSpreadsheetMethod(values.creationType) && {
                    needsAttentionCount:
                      getArrayLength(values.documents) -
                      getReadyDocumentsCount(values.documents),
                  })}
                  onNext={handleNext}
                  onBack={() => goToStep(RFI_CREATE_STEPS.DOCUMENTS)}
                />
              </form>
            )}
          />
        </>
      )}
    </div>
  );
};

export default RfiCreate;
